import { useEffect, useState } from "react";
import { superadminApi } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { UserSwitch, Buildings, MagnifyingGlass, Plus, TrashSimple, CaretDown, CaretUp } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtDate = (s) => (s ? new Date(s).toLocaleString() : "—");

export default function SupportPanel() {
    const [users, setUsers] = useState([]);
    const [q, setQ] = useState("");
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        superadminApi.users().then(setUsers).catch(() => toast.error("Failed to load users"));
    }, []);

    const filtered = users.filter((u) =>
        !q || (u.email || "").toLowerCase().includes(q.toLowerCase()) || (u.name || "").toLowerCase().includes(q.toLowerCase())
    );

    return (
        <div className="grid grid-cols-12 gap-4" data-testid="support-panel">
            <div className="col-span-12 lg:col-span-3">
                <div className="border border-border rounded-md bg-white overflow-hidden">
                    <div className="p-3 border-b border-border">
                        <div className="relative">
                            <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
                            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users…" className="pl-8 h-9" data-testid="support-user-search" />
                        </div>
                    </div>
                    <div className="max-h-[60vh] overflow-y-auto scrollbar-slim">
                        {filtered.map((u) => (
                            <button
                                key={u.user_id}
                                onClick={() => setSelected(u)}
                                className={`w-full text-left px-3 py-2.5 border-b border-border/60 hover:bg-muted/40 transition-colors ${selected?.user_id === u.user_id ? "bg-muted/60" : ""}`}
                                data-testid={`support-user-${u.user_id}`}
                            >
                                <div className="text-sm truncate">{u.name || u.email}</div>
                                <div className="text-[11px] text-muted-foreground truncate">{u.email}</div>
                                <div className="flex items-center gap-2 mt-1">
                                    <span className={`chip ${u.role === "superadmin" ? "chip-ai" : u.role === "admin" ? "chip-external" : "chip-doc"}`}>{u.role || "user"}</span>
                                    <span className="text-[10px] text-muted-foreground">{u.last_login ? new Date(u.last_login).toLocaleDateString() : "never signed in"}</span>
                                </div>
                            </button>
                        ))}
                        {!filtered.length && <div className="p-4 text-sm text-muted-foreground">No users match.</div>}
                    </div>
                </div>
            </div>
            <div className="col-span-12 lg:col-span-9">
                {!selected ? (
                    <div className="border border-dashed border-border rounded-md p-10 text-center text-sm text-muted-foreground" data-testid="support-empty-state">
                        Select a user to review their queries, results and activity — or to help them set up their chamber.
                    </div>
                ) : (
                    <UserDetail key={selected.user_id} target={selected} />
                )}
            </div>
        </div>
    );
}

function UserDetail({ target }) {
    const [overview, setOverview] = useState(null);
    const [setupOpen, setSetupOpen] = useState(false);

    useEffect(() => {
        superadminApi.overview(target.user_id).then(setOverview).catch(() => toast.error("Failed to load overview"));
    }, [target.user_id]);

    const impersonate = async () => {
        if (!window.confirm(`Enter Support Mode as ${target.email}? You will see the app exactly as they do. All actions are audit-logged.`)) return;
        try {
            await superadminApi.impersonate(target.user_id);
            window.location.href = "/dashboard";
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Impersonation failed");
        }
    };

    return (
        <div className="border border-border rounded-md bg-white p-4" data-testid="support-user-detail">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div>
                    <div className="font-serif text-2xl">{target.name || target.email}</div>
                    <div className="text-xs text-muted-foreground">{target.email} · last login {fmtDate(target.last_login)}</div>
                    {overview && (
                        <div className="flex flex-wrap gap-2 mt-2">
                            {Object.entries(overview.counts).map(([k, v]) => (
                                <span key={k} className="chip chip-doc text-[10px]">{k.replace(/_/g, " ")}: {v}</span>
                            ))}
                        </div>
                    )}
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setSetupOpen(true)} data-testid="support-setup-chamber-btn">
                        <Buildings size={14} className="mr-1" /> Setup chamber
                    </Button>
                    <Button size="sm" onClick={impersonate} data-testid="support-impersonate-btn">
                        <UserSwitch size={14} className="mr-1" /> Impersonate
                    </Button>
                </div>
            </div>

            <Tabs defaultValue="research">
                <TabsList>
                    <TabsTrigger value="research" data-testid="support-tab-research">Research</TabsTrigger>
                    <TabsTrigger value="interactions" data-testid="support-tab-interactions">AI Q&A</TabsTrigger>
                    <TabsTrigger value="activity" data-testid="support-tab-activity">Activity</TabsTrigger>
                    <TabsTrigger value="chamber" data-testid="support-tab-chamber">Chamber</TabsTrigger>
                </TabsList>
                <TabsContent value="research" className="mt-3"><ResearchLog uid={target.user_id} /></TabsContent>
                <TabsContent value="interactions" className="mt-3"><InteractionLog uid={target.user_id} /></TabsContent>
                <TabsContent value="activity" className="mt-3"><ActivityLog uid={target.user_id} /></TabsContent>
                <TabsContent value="chamber" className="mt-3"><ChamberInfo overview={overview} /></TabsContent>
            </Tabs>

            <ChamberSetupDialog open={setupOpen} onOpenChange={setSetupOpen} target={target} />
        </div>
    );
}

function Expandable({ summary, children, testid }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="border border-border rounded-md" data-testid={testid}>
            <button onClick={() => setOpen(!open)} className="w-full flex items-start justify-between gap-3 p-3 text-left hover:bg-muted/30">
                <div className="min-w-0 flex-1">{summary}</div>
                {open ? <CaretUp size={14} className="mt-1 shrink-0" /> : <CaretDown size={14} className="mt-1 shrink-0" />}
            </button>
            {open && <div className="border-t border-border p-3">{children}</div>}
        </div>
    );
}

function ResearchLog({ uid }) {
    const [entries, setEntries] = useState(null);
    useEffect(() => {
        superadminApi.research(uid).then(setEntries).catch(() => toast.error("Failed to load research log"));
    }, [uid]);
    if (!entries) return <div className="text-sm text-muted-foreground p-4">Loading…</div>;
    if (!entries.length) return <div className="text-sm text-muted-foreground p-4" data-testid="support-research-empty">No research queries yet.</div>;
    return (
        <div className="space-y-2 max-h-[55vh] overflow-y-auto scrollbar-slim pr-1" data-testid="support-research-list">
            {entries.map((e, i) => (
                <Expandable
                    key={i}
                    testid={`support-research-entry-${i}`}
                    summary={
                        <>
                            <div className="text-sm font-medium truncate">{e.query || "(empty query)"}</div>
                            <div className="flex flex-wrap items-center gap-2 mt-1 text-[11px] text-muted-foreground">
                                <span className={`chip ${e.status === "completed" ? "chip-doc" : e.status === "failed" ? "chip-warn" : "chip-external"}`}>{e.status}</span>
                                {e.provider && <span>{e.provider}</span>}
                                {e.confidence && <span>confidence: {e.confidence}</span>}
                                <span>{fmtDate(e.created_at)}</span>
                            </div>
                        </>
                    }
                >
                    {e.error && <div className="text-xs text-destructive mb-2">Error: {e.error}</div>}
                    <div className="text-sm whitespace-pre-wrap max-h-[40vh] overflow-y-auto scrollbar-slim">{e.answer || "(no answer recorded)"}</div>
                    {!!(e.citations || []).length && (
                        <div className="mt-3 border-t border-border pt-2">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Citations ({e.citations.length})</div>
                            {e.citations.map((c, j) => (
                                <div key={j} className="text-xs py-1 border-b border-border/50 last:border-0">
                                    <span className="font-medium">{c.title || c.case_name || "—"}</span>
                                    {c.citation && <span className="text-muted-foreground"> · {c.citation}</span>}
                                    {c.court && <span className="text-muted-foreground"> · {c.court}</span>}
                                    {c.url && <a href={c.url} target="_blank" rel="noreferrer" className="text-primary ml-1 underline">link</a>}
                                </div>
                            ))}
                        </div>
                    )}
                    {e.jurisdiction_notes && <div className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap">{e.jurisdiction_notes}</div>}
                </Expandable>
            ))}
        </div>
    );
}

function InteractionLog({ uid }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        superadminApi.interactions(uid).then(setData).catch(() => toast.error("Failed to load interactions"));
    }, [uid]);
    if (!data) return <div className="text-sm text-muted-foreground p-4">Loading…</div>;
    const { interactions = [], jobs = [] } = data;
    if (!interactions.length && !jobs.length)
        return <div className="text-sm text-muted-foreground p-4" data-testid="support-interactions-empty">No AI interactions recorded yet. Document-chat Q&A is logged from now on.</div>;
    return (
        <div className="space-y-4 max-h-[55vh] overflow-y-auto scrollbar-slim pr-1" data-testid="support-interactions-list">
            {!!interactions.length && (
                <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Document / matter Q&A ({interactions.length})</div>
                    <div className="space-y-2">
                        {interactions.map((it) => (
                            <Expandable
                                key={it.id}
                                testid={`support-interaction-${it.id}`}
                                summary={
                                    <>
                                        <div className="text-sm truncate">{it.query}</div>
                                        <div className="text-[11px] text-muted-foreground mt-1">
                                            <span className="chip chip-doc mr-2">{it.feature}</span>
                                            {it.meta?.document && <span className="mr-2">{it.meta.document}</span>}
                                            {fmtDate(it.created_at)}
                                        </div>
                                    </>
                                }
                            >
                                <div className="text-sm whitespace-pre-wrap max-h-[35vh] overflow-y-auto scrollbar-slim">{it.response}</div>
                            </Expandable>
                        ))}
                    </div>
                </div>
            )}
            {!!jobs.length && (
                <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">AI jobs — drafting, strategy, analysis ({jobs.length})</div>
                    <div className="space-y-2">
                        {jobs.map((j) => (
                            <Expandable
                                key={j.job_id}
                                testid={`support-job-${j.job_id}`}
                                summary={
                                    <>
                                        <div className="text-sm truncate">{j.job_type} — {(j.payload?.query || j.payload?.instructions || j.payload?.title || JSON.stringify(j.payload || {})).slice(0, 120)}</div>
                                        <div className="text-[11px] text-muted-foreground mt-1">
                                            <span className={`chip ${j.status === "completed" ? "chip-doc" : j.status === "failed" ? "chip-warn" : "chip-external"} mr-2`}>{j.status}</span>
                                            {fmtDate(j.created_at)}
                                        </div>
                                    </>
                                }
                            >
                                {j.error && <div className="text-xs text-destructive mb-2">Error: {j.error}</div>}
                                <pre className="text-xs whitespace-pre-wrap max-h-[35vh] overflow-y-auto scrollbar-slim font-sans">{typeof j.result === "string" ? j.result : JSON.stringify(j.result || {}, null, 2).slice(0, 8000)}</pre>
                            </Expandable>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function ActivityLog({ uid }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        superadminApi.activity(uid).then(setData).catch(() => toast.error("Failed to load activity"));
    }, [uid]);
    if (!data) return <div className="text-sm text-muted-foreground p-4">Loading…</div>;
    const sections = [
        ["Matters", data.matters, (m) => `${m.title} · ${m.status || ""} ${m.court ? "· " + m.court : ""}`],
        ["Documents", data.documents, (d) => `${d.original_filename} · ${d.processing_status} ${d.doc_type ? "· " + d.doc_type : ""}`],
        ["Drafts", data.drafts, (d) => `${d.title || "(untitled)"} · ${d.draft_type || ""}`],
        ["Invoices", data.invoices, (i) => `${i.invoice_no || i.id?.slice(0, 8)} · ₹${Number(i.total || 0).toLocaleString("en-IN")} · ${i.status}`],
        ["Tasks", data.tasks, (t) => `${t.title} · ${t.status}${t.due_date ? " · due " + new Date(t.due_date).toLocaleDateString() : ""}`],
    ];
    return (
        <div className="space-y-4 max-h-[55vh] overflow-y-auto scrollbar-slim pr-1" data-testid="support-activity">
            {sections.map(([label, rows, fmt]) => (
                <div key={label}>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">{label} ({rows?.length || 0})</div>
                    {!rows?.length ? <div className="text-xs text-muted-foreground">None</div> : (
                        <div className="border border-border rounded-md divide-y divide-border/60">
                            {rows.map((r, i) => <div key={r.id || i} className="px-3 py-1.5 text-xs truncate">{fmt(r)}</div>)}
                        </div>
                    )}
                </div>
            ))}
            <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Recent audit events ({data.audit?.length || 0})</div>
                <div className="border border-border rounded-md divide-y divide-border/60">
                    {(data.audit || []).map((a, i) => (
                        <div key={a.id || i} className="px-3 py-1.5 text-xs flex gap-2">
                            <span className="text-muted-foreground font-mono whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</span>
                            <span className="chip chip-external">{a.action}</span>
                            <span className="text-muted-foreground truncate">{JSON.stringify(a.meta || {}).slice(0, 120)}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function ChamberInfo({ overview }) {
    if (!overview) return <div className="text-sm text-muted-foreground p-4">Loading…</div>;
    const chambers = overview.chambers || [];
    return (
        <div data-testid="support-chamber-info">
            <div className="text-xs text-muted-foreground mb-3">
                Sign-in access: {overview.allowlisted ? <span className="chip chip-doc">allowlisted</span> : <span className="chip chip-warn">not on allowlist</span>}
            </div>
            {!chambers.length ? (
                <div className="text-sm text-muted-foreground">Not a member of any chamber. Use "Setup chamber" to create one for them.</div>
            ) : (
                <div className="grid md:grid-cols-2 gap-3">
                    {chambers.map((c) => (
                        <div key={c.id} className="border border-border rounded-md p-3">
                            <div className="font-serif text-lg">{c.name}</div>
                            <div className="text-xs text-muted-foreground">{c.description || "—"}</div>
                            <div className="text-xs mt-2">
                                Their role: <span className="chip chip-doc">{c.membership?.role}</span> · status {c.membership?.status}
                            </div>
                            <div className="text-[11px] text-muted-foreground mt-1">Owner: {c.owner_email}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function ChamberSetupDialog({ open, onOpenChange, target }) {
    const [name, setName] = useState("");
    const [desc, setDesc] = useState("");
    const [members, setMembers] = useState([]);
    const [addToAllowlist, setAddToAllowlist] = useState(true);
    const [saving, setSaving] = useState(false);

    const addMember = () => setMembers([...members, { email: "", role: "associate" }]);
    const setMember = (i, patch) => setMembers(members.map((m, j) => (j === i ? { ...m, ...patch } : m)));
    const removeMember = (i) => setMembers(members.filter((_, j) => j !== i));

    const submit = async () => {
        if (!name.trim()) { toast.error("Chamber name required"); return; }
        const clean = members.filter((m) => m.email.trim());
        setSaving(true);
        try {
            const res = await superadminApi.chamberSetup(target.user_id, {
                name: name.trim(),
                description: desc.trim(),
                members: clean,
                add_to_allowlist: addToAllowlist,
            });
            toast.success(`Chamber "${res.chamber.name}" created for ${target.email}${clean.length ? ` with ${clean.length} member(s) invited` : ""}`);
            onOpenChange(false);
            setName(""); setDesc(""); setMembers([]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Setup failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="chamber-setup-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif">Setup chamber for {target.email}</DialogTitle>
                    <DialogDescription>Creates a chamber owned by this user and invites the members you list below.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <label className="text-xs text-muted-foreground">Chamber name</label>
                        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Singh & Associates" data-testid="chamber-setup-name" />
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground">Description (optional)</label>
                        <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={2} data-testid="chamber-setup-desc" />
                    </div>
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className="text-xs text-muted-foreground">Members to invite</label>
                            <Button variant="outline" size="sm" onClick={addMember} data-testid="chamber-setup-add-member"><Plus size={12} /> Add</Button>
                        </div>
                        {members.map((m, i) => (
                            <div key={i} className="flex gap-2 mb-2">
                                <Input value={m.email} onChange={(e) => setMember(i, { email: e.target.value })} placeholder="member@example.com" className="flex-1" data-testid={`chamber-setup-member-email-${i}`} />
                                <Select value={m.role} onValueChange={(v) => setMember(i, { role: v })}>
                                    <SelectTrigger className="w-32" data-testid={`chamber-setup-member-role-${i}`}><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="admin">admin</SelectItem>
                                        <SelectItem value="associate">associate</SelectItem>
                                        <SelectItem value="paralegal">paralegal</SelectItem>
                                    </SelectContent>
                                </Select>
                                <button onClick={() => removeMember(i)} className="text-muted-foreground hover:text-destructive"><TrashSimple size={14} /></button>
                            </div>
                        ))}
                        <label className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                            <input type="checkbox" checked={addToAllowlist} onChange={(e) => setAddToAllowlist(e.target.checked)} data-testid="chamber-setup-allowlist-check" />
                            Also approve invited emails for sign-in (allowlist)
                        </label>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} data-testid="chamber-setup-submit">{saving ? "Creating…" : "Create chamber"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
